import React, { useState } from 'react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend
} from 'recharts'
import {
  TrendingUp,
  TrendingDown,
  Target,
  Award,
  AlertCircle,
  Info,
  BarChart3,
  Activity
} from 'lucide-react'
import type { RiskAdjustedReturns, RiskMetricsDisplayProps } from '@/types/risk-assessment'

export function RiskAdjustedReturnsMetrics({
  risk_adjusted_returns,
  benchmark_data
}: RiskMetricsDisplayProps) {
  const [activeMetric, setActiveMetric] = useState<'sharpe' | 'sortino' | 'calmar' | 'volatility'>('sharpe')

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`
  }

  const formatRatio = (value: number) => {
    return value.toFixed(3)
  }

  const getPerformanceColor = (value: number, benchmark: number) => {
    if (value > benchmark * 1.1) return 'text-green-600'
    if (value > benchmark * 0.9) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getPerformanceBg = (value: number, benchmark: number) => {
    if (value > benchmark * 1.1) return 'bg-green-50 border-green-200'
    if (value > benchmark * 0.9) return 'bg-yellow-50 border-yellow-200'
    return 'bg-red-50 border-red-200'
  }

  const getRatioQuality = (ratio: number, type: 'sharpe' | 'sortino' | 'calmar') => {
    const thresholds = {
      sharpe: { excellent: 2.0, good: 1.0, fair: 0.5 },
      sortino: { excellent: 2.5, good: 1.5, fair: 0.75 },
      calmar: { excellent: 1.0, good: 0.5, fair: 0.25 }
    }

    const t = thresholds[type]
    if (ratio >= t.excellent) return { label: 'Excellent', color: 'text-green-600', bg: 'bg-green-100' }
    if (ratio >= t.good) return { label: 'Good', color: 'text-blue-600', bg: 'bg-blue-100' }
    if (ratio >= t.fair) return { label: 'Fair', color: 'text-yellow-600', bg: 'bg-yellow-100' }
    return { label: 'Poor', color: 'text-red-600', bg: 'bg-red-100' }
  }

  // Prepare chart data
  const riskReturnData = [
    {
      metric: 'Your Portfolio',
      return: risk_adjusted_returns.risk_adjusted_irr,
      volatility: risk_adjusted_returns.volatility_metrics.annual_volatility,
      sharpe: risk_adjusted_returns.sharpe_ratio
    },
    {
      metric: 'Market Benchmark',
      return: risk_adjusted_returns.benchmark_comparison.market_index === 'REIT' ? 8.5 : 10.2,
      volatility: 15.8,
      sharpe: 0.54
    }
  ]

  const ratioComparisonData = [
    {
      name: 'Sharpe Ratio',
      portfolio: risk_adjusted_returns.sharpe_ratio,
      benchmark: 0.54,
      industry_avg: 0.68
    },
    {
      name: 'Sortino Ratio',
      portfolio: risk_adjusted_returns.sortino_ratio,
      benchmark: 0.72,
      industry_avg: 0.89
    },
    {
      name: 'Calmar Ratio',
      portfolio: risk_adjusted_returns.calmar_ratio,
      benchmark: 0.45,
      industry_avg: 0.58
    },
    {
      name: 'Information Ratio',
      portfolio: risk_adjusted_returns.information_ratio,
      benchmark: 0.0,
      industry_avg: 0.25
    }
  ]

  const volatilityData = [
    {
      metric: 'Annual Volatility',
      value: risk_adjusted_returns.volatility_metrics.annual_volatility,
      benchmark: 15.8,
      target: 12.0
    },
    {
      metric: 'Downside Deviation',
      value: risk_adjusted_returns.volatility_metrics.downside_deviation,
      benchmark: 11.2,
      target: 8.0
    },
    {
      metric: 'Maximum Drawdown',
      value: risk_adjusted_returns.volatility_metrics.maximum_drawdown,
      benchmark: 18.5,
      target: 15.0
    },
    {
      metric: 'Beta to Market',
      value: risk_adjusted_returns.volatility_metrics.beta_to_market,
      benchmark: 1.0,
      target: 0.8
    }
  ]

  const radarData = [
    {
      metric: 'Return',
      portfolio: Math.min(risk_adjusted_returns.risk_adjusted_irr / 15 * 100, 100),
      benchmark: 67
    },
    {
      metric: 'Risk Management',
      portfolio: Math.max(100 - risk_adjusted_returns.volatility_metrics.annual_volatility * 5, 0),
      benchmark: 70
    },
    {
      metric: 'Consistency',
      portfolio: Math.max(100 - risk_adjusted_returns.volatility_metrics.downside_deviation * 8, 0),
      benchmark: 65
    },
    {
      metric: 'Risk Efficiency',
      portfolio: Math.min(risk_adjusted_returns.sharpe_ratio * 40, 100),
      benchmark: 55
    },
    {
      metric: 'Downside Protection',
      portfolio: Math.min(risk_adjusted_returns.sortino_ratio * 35, 100),
      benchmark: 60
    }
  ]

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {
                entry.dataKey === 'volatility' || entry.name.includes('Return') || entry.name.includes('Volatility')
                  ? formatPercentage(entry.value)
                  : formatRatio(entry.value)
              }
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  return (
    <div className="space-y-8">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sharpe Ratio */}
        <div className={`rounded-lg border p-6 ${getPerformanceBg(risk_adjusted_returns.sharpe_ratio, 0.54)}`}>
          <div className="flex items-center justify-between mb-2">
            <Award className="w-6 h-6 text-blue-600" />
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRatioQuality(risk_adjusted_returns.sharpe_ratio, 'sharpe').bg} ${getRatioQuality(risk_adjusted_returns.sharpe_ratio, 'sharpe').color}`}>
              {getRatioQuality(risk_adjusted_returns.sharpe_ratio, 'sharpe').label}
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatRatio(risk_adjusted_returns.sharpe_ratio)}
          </div>
          <div className="text-sm text-gray-600">Sharpe Ratio</div>
          <div className="text-xs text-gray-500 mt-1">
            Benchmark: {formatRatio(0.54)}
          </div>
        </div>

        {/* Sortino Ratio */}
        <div className={`rounded-lg border p-6 ${getPerformanceBg(risk_adjusted_returns.sortino_ratio, 0.72)}`}>
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-6 h-6 text-green-600" />
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRatioQuality(risk_adjusted_returns.sortino_ratio, 'sortino').bg} ${getRatioQuality(risk_adjusted_returns.sortino_ratio, 'sortino').color}`}>
              {getRatioQuality(risk_adjusted_returns.sortino_ratio, 'sortino').label}
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatRatio(risk_adjusted_returns.sortino_ratio)}
          </div>
          <div className="text-sm text-gray-600">Sortino Ratio</div>
          <div className="text-xs text-gray-500 mt-1">
            Benchmark: {formatRatio(0.72)}
          </div>
        </div>

        {/* Annual Volatility */}
        <div className={`rounded-lg border p-6 ${getPerformanceBg(15.8, risk_adjusted_returns.volatility_metrics.annual_volatility)}`}>
          <div className="flex items-center justify-between mb-2">
            <Activity className="w-6 h-6 text-orange-600" />
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              risk_adjusted_returns.volatility_metrics.annual_volatility < 12 ? 'bg-green-100 text-green-600' :
              risk_adjusted_returns.volatility_metrics.annual_volatility < 18 ? 'bg-yellow-100 text-yellow-600' :
              'bg-red-100 text-red-600'
            }`}>
              {risk_adjusted_returns.volatility_metrics.annual_volatility < 12 ? 'Low' :
               risk_adjusted_returns.volatility_metrics.annual_volatility < 18 ? 'Moderate' : 'High'}
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatPercentage(risk_adjusted_returns.volatility_metrics.annual_volatility)}
          </div>
          <div className="text-sm text-gray-600">Annual Volatility</div>
          <div className="text-xs text-gray-500 mt-1">
            Market: {formatPercentage(15.8)}
          </div>
        </div>

        {/* Maximum Drawdown */}
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <div className="flex items-center justify-between mb-2">
            <TrendingDown className="w-6 h-6 text-red-600" />
            <AlertCircle className="w-4 h-4 text-red-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {formatPercentage(risk_adjusted_returns.volatility_metrics.maximum_drawdown)}
          </div>
          <div className="text-sm text-gray-600">Maximum Drawdown</div>
          <div className="text-xs text-gray-500 mt-1">
            Market: {formatPercentage(18.5)}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'sharpe', name: 'Risk-Return Ratios', icon: Award },
            { id: 'volatility', name: 'Volatility Analysis', icon: Activity },
            { id: 'sortino', name: 'Downside Risk', icon: TrendingDown },
            { id: 'calmar', name: 'Performance Analysis', icon: BarChart3 }
          ].map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveMetric(tab.id as any)}
                className={`
                  flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm
                  ${activeMetric === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-8">
        {activeMetric === 'sharpe' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Ratio Comparison Chart */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk-Adjusted Ratio Comparison</h4>
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <BarChart data={ratioComparisonData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="portfolio" fill="#3b82f6" name="Your Portfolio" />
                    <Bar dataKey="benchmark" fill="#6b7280" name="Market Benchmark" />
                    <Bar dataKey="industry_avg" fill="#10b981" name="Industry Average" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Performance Radar */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Portfolio Performance Profile</h4>
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="metric" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar
                      name="Your Portfolio"
                      dataKey="portfolio"
                      stroke="#3b82f6"
                      fill="#3b82f6"
                      fillOpacity={0.3}
                    />
                    <Radar
                      name="Benchmark"
                      dataKey="benchmark"
                      stroke="#6b7280"
                      fill="#6b7280"
                      fillOpacity={0.1}
                    />
                    <Legend />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {activeMetric === 'volatility' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Volatility Metrics */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Volatility Analysis</h4>
              <div className="space-y-6">
                {volatilityData.map((metric) => (
                  <div key={metric.metric} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">{metric.metric}</span>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-gray-600">
                          Target: {metric.metric.includes('Beta') ? formatRatio(metric.target) : formatPercentage(metric.target)}
                        </span>
                        <span className={`font-bold ${getPerformanceColor(
                          metric.metric.includes('Drawdown') || metric.metric.includes('Volatility') || metric.metric.includes('Deviation')
                            ? metric.target
                            : metric.value,
                          metric.metric.includes('Drawdown') || metric.metric.includes('Volatility') || metric.metric.includes('Deviation')
                            ? metric.value
                            : metric.target
                        )}`}>
                          {metric.metric.includes('Beta') ? formatRatio(metric.value) : formatPercentage(metric.value)}
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${
                          metric.metric.includes('Drawdown') || metric.metric.includes('Volatility') || metric.metric.includes('Deviation')
                            ? metric.value <= metric.target ? 'bg-green-500' : 'bg-red-500'
                            : metric.value >= metric.target ? 'bg-green-500' : 'bg-red-500'
                        }`}
                        style={{
                          width: `${Math.min(
                            metric.metric.includes('Beta')
                              ? (metric.value / 2) * 100
                              : (metric.value / (metric.metric.includes('Drawdown') ? 30 : 25)) * 100,
                            100
                          )}%`
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Low Risk</span>
                      <span>High Risk</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk-Return Scatter */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Risk vs Return Analysis</h4>
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <LineChart data={riskReturnData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="volatility"
                      label={{ value: 'Risk (Volatility %)', position: 'insideBottom', offset: -5 }}
                      tickFormatter={formatPercentage}
                    />
                    <YAxis
                      dataKey="return"
                      label={{ value: 'Return (%)', angle: -90, position: 'insideLeft' }}
                      tickFormatter={formatPercentage}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="return"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      dot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Benchmark Comparison */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Benchmark Analysis</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Excess Return</span>
              </div>
              <div className="text-2xl font-bold text-blue-900">
                {formatPercentage(risk_adjusted_returns.benchmark_comparison.excess_return)}
              </div>
              <div className="text-sm text-blue-700">vs {risk_adjusted_returns.benchmark_comparison.market_index}</div>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-900">Alpha</span>
              </div>
              <div className="text-2xl font-bold text-green-900">
                {formatPercentage(risk_adjusted_returns.benchmark_comparison.alpha)}
              </div>
              <div className="text-sm text-green-700">Risk-adjusted excess return</div>
            </div>

            <div className="p-4 bg-yellow-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-900">Tracking Error</span>
              </div>
              <div className="text-2xl font-bold text-yellow-900">
                {formatPercentage(risk_adjusted_returns.benchmark_comparison.tracking_error)}
              </div>
              <div className="text-sm text-yellow-700">Return volatility vs benchmark</div>
            </div>
          </div>
        </div>

        {/* Educational Content */}
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
          <div className="flex items-start space-x-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Understanding Risk-Adjusted Returns</h4>
              <div className="space-y-2 text-sm text-blue-800">
                <p><strong>Sharpe Ratio:</strong> Measures excess return per unit of total risk. Higher is better (&gt;1.0 is good, &gt;2.0 is excellent).</p>
                <p><strong>Sortino Ratio:</strong> Similar to Sharpe but only considers downside volatility. Better for real estate investors concerned with losses.</p>
                <p><strong>Calmar Ratio:</strong> Annual return divided by maximum drawdown. Shows return efficiency relative to worst-case scenario.</p>
                <p><strong>Information Ratio:</strong> Measures active return relative to benchmark per unit of tracking error.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}